import { useState, useEffect, useRef } from "react"
import { UseFormReturn, UseFormSetValue } from "react-hook-form"
import { ProtocolPeriod, ProtocolAssessment } from "../../../services/studyProtocolApi"
import { generateVisitId } from "./constants"
import { ProtocolFormData } from "./schema"

export type VisitField = "visit_number" | "study_week" | "study_day" | "window_days"

interface EditingCell {
  visitId: string
  field: VisitField
}

/**
 * Custom hook that manages all state and handlers for the AssessmentsGrid component.
 *
 * Encapsulates CRUD operations for periods, visits, categories, and assessments, as well as UI
 * state for editing, menus, and hover interactions.
 *
 * @param form - React Hook Form instance for the protocol form.
 * @param partIndex - Index of the protocol part being edited.
 * @returns State values, setters, and handler functions for the grid
 */
export const useAssessmentsGrid = (form: UseFormReturn<ProtocolFormData>, partIndex: number) => {
  const { watch } = form
  // Grid mutations must mark the form dirty so the form-level leave guard knows about them.
  const setValue: UseFormSetValue<ProtocolFormData> = (name, value) =>
    form.setValue(name, value, { shouldDirty: true })
  const periods: ProtocolPeriod[] = watch(`parts.${partIndex}.periods`) || []
  const assessments: ProtocolAssessment[] = watch(`parts.${partIndex}.assessments`) || []

  const allVisits = periods.flatMap((p) => p.visits.map((v) => ({ ...v, periodName: p.name })))
  const categories = [...new Set(assessments.map((a) => a.category || "Uncategorized"))]

  // UI state
  const [newCategoryName, setNewCategoryName] = useState<string | null>(null)
  const [addingAssessment, setAddingAssessment] = useState<string | null>(null)
  const [newAssessmentName, setNewAssessmentName] = useState("")
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
  const [cellDraft, setCellDraft] = useState("")
  const [showSavePrompt, setShowSavePrompt] = useState(false)
  const [editingPeriod, setEditingPeriod] = useState<number | null>(null)
  const [editingCategory, setEditingCategory] = useState<string | null>(null)
  const [hoveringVisit, setHoveringVisit] = useState<string | null>(null)

  const theadRef = useRef<HTMLTableSectionElement>(null)
  const [theadHeight, setTheadHeight] = useState(0)

  // Update thead height for sticky category positioning
  useEffect(() => {
    if (!theadRef.current) return
    const updateHeight = () => setTheadHeight(theadRef.current?.offsetHeight || 0)
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(theadRef.current)
    return () => observer.disconnect()
  }, [])

  // ─────────────────────────────────────────────────────────────────────────────
  // Per-Cell Editing
  // ─────────────────────────────────────────────────────────────────────────────

  const visitFieldValue = (visitId: string, field: VisitField) =>
    allVisits.find((v) => v.id === visitId)?.[field] || ""

  const cellDirty =
    editingCell !== null && cellDraft !== visitFieldValue(editingCell.visitId, editingCell.field)

  const startCellEdit = (visitId: string, field: VisitField) => {
    if (editingCell) {
      if (editingCell.visitId === visitId && editingCell.field === field) return
      // Leaving a dirty cell requires an explicit save/cancel decision first.
      if (cellDirty) {
        setShowSavePrompt(true)
        return
      }
    }
    setEditingCell({ visitId, field })
    setCellDraft(visitFieldValue(visitId, field))
  }

  const commitCellEdit = () => {
    if (editingCell) updateVisit(editingCell.visitId, editingCell.field, cellDraft)
    setEditingCell(null)
    setShowSavePrompt(false)
  }

  const cancelCellEdit = () => {
    setEditingCell(null)
    setShowSavePrompt(false)
  }

  const keepEditingCell = () => setShowSavePrompt(false)

  // Clicking outside the editing cell: exit if clean, prompt to save if dirty. Capture-phase so a
  // dirty cell can swallow the outside click entirely — otherwise a click on e.g. the form's
  // submit button would both open the prompt and submit the form with the uncommitted draft.
  useEffect(() => {
    if (!editingCell || showSavePrompt) return

    const handleClickCapture = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest(".cell-editing")) return
      if (cellDirty) {
        e.preventDefault()
        e.stopPropagation()
        setShowSavePrompt(true)
      } else {
        setEditingCell(null)
      }
    }

    document.addEventListener("click", handleClickCapture, true)
    return () => document.removeEventListener("click", handleClickCapture, true)
  }, [editingCell, showSavePrompt, cellDirty])

  // ─────────────────────────────────────────────────────────────────────────────
  // Period Management
  // ─────────────────────────────────────────────────────────────────────────────

  const addPeriod = (afterIndex?: number) => {
    const newPeriod = {
      name: "New Period",
      visits: [
        { id: generateVisitId(), visit_number: "", study_week: "", study_day: "", window_days: "" },
      ],
    }

    if (afterIndex !== undefined) {
      const updated = [...periods]
      updated.splice(afterIndex + 1, 0, newPeriod)
      setValue(`parts.${partIndex}.periods`, updated)
      setEditingPeriod(afterIndex + 1)
    } else {
      setValue(`parts.${partIndex}.periods`, [...periods, newPeriod])
      setEditingPeriod(periods.length)
    }
  }

  const renamePeriod = (periodIdx: number, newName: string) => {
    if (newName.trim()) {
      const updated = [...periods]
      updated[periodIdx] = { ...updated[periodIdx], name: newName.trim() }
      setValue(`parts.${partIndex}.periods`, updated)
    }
    setEditingPeriod(null)
  }

  const deletePeriod = (periodIdx: number) => {
    const periodVisitCount = periods[periodIdx].visits.length
    let startPosition = 1
    for (let i = 0; i < periodIdx; i++) {
      startPosition += periods[i].visits.length
    }
    const endPosition = startPosition + periodVisitCount - 1

    const updated = assessments.map((a) => ({
      ...a,
      scheduled_visits: a.scheduled_visits
        ?.filter((pos) => pos < startPosition || pos > endPosition)
        .map((pos) => (pos > endPosition ? pos - periodVisitCount : pos)),
    }))
    setValue(`parts.${partIndex}.assessments`, updated)

    setValue(
      `parts.${partIndex}.periods`,
      periods.filter((_, i) => i !== periodIdx),
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Visit Management
  // ─────────────────────────────────────────────────────────────────────────────

  const addVisit = (periodIdx: number) => {
    const updated = [...periods]
    const newVisit = {
      id: generateVisitId(),
      visit_number: "",
      study_week: "",
      study_day: "",
      window_days: "",
    }
    updated[periodIdx] = {
      ...updated[periodIdx],
      visits: [...updated[periodIdx].visits, newVisit],
    }
    setValue(`parts.${partIndex}.periods`, updated)
    setEditingCell({ visitId: newVisit.id, field: "visit_number" })
    setCellDraft("")
  }

  const updateVisit = (visitId: string, field: string, value: string) => {
    const updated = periods.map((p) => ({
      ...p,
      visits: p.visits.map((v) => (v.id === visitId ? { ...v, [field]: value } : v)),
    }))
    setValue(`parts.${partIndex}.periods`, updated)
  }

  const deleteVisit = (periodIdx: number, visitIdx: number) => {
    let position = 1
    for (let i = 0; i < periodIdx; i++) {
      position += periods[i].visits.length
    }
    position += visitIdx

    const updatedAssessments = assessments.map((a) => ({
      ...a,
      scheduled_visits: a.scheduled_visits
        ?.filter((pos) => pos !== position)
        .map((pos) => (pos > position ? pos - 1 : pos)),
    }))
    setValue(`parts.${partIndex}.assessments`, updatedAssessments)

    const updated = periods.map((p, pIdx) =>
      pIdx === periodIdx ? { ...p, visits: p.visits.filter((_, vIdx) => vIdx !== visitIdx) } : p,
    )
    setValue(`parts.${partIndex}.periods`, updated)

    if (editingCell?.visitId === periods[periodIdx].visits[visitIdx]?.id) cancelCellEdit()
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Category Management
  // ─────────────────────────────────────────────────────────────────────────────

  const addCategory = () => {
    const categoryName = "New Category"
    setValue(`parts.${partIndex}.assessments`, [
      ...assessments,
      { category: categoryName, name: "New Assessment", scheduled_visits: [] },
    ])
    setNewCategoryName(categoryName)
  }

  const renameNewCategory = (newName: string) => {
    if (newCategoryName && newName.trim() && newName.trim() !== newCategoryName) {
      const updated = assessments.map((a) =>
        a.category === newCategoryName ? { ...a, category: newName.trim() } : a,
      )
      setValue(`parts.${partIndex}.assessments`, updated)
    }
    setNewCategoryName(null)
  }

  const renameCategory = (oldName: string, newName: string) => {
    if (newName.trim() && newName.trim() !== oldName) {
      const updated = assessments.map((a) =>
        (a.category || "Uncategorized") === oldName ? { ...a, category: newName.trim() } : a,
      )
      setValue(`parts.${partIndex}.assessments`, updated)
    }
    setEditingCategory(null)
  }

  const deleteCategory = (category: string) => {
    setValue(
      `parts.${partIndex}.assessments`,
      assessments.filter((a) => (a.category || "Uncategorized") !== category),
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Assessment Management
  // ─────────────────────────────────────────────────────────────────────────────

  const addAssessmentToCategory = (category: string) => {
    if (!newAssessmentName.trim()) return

    setValue(`parts.${partIndex}.assessments`, [
      ...assessments,
      { category, name: newAssessmentName.trim(), scheduled_visits: [] },
    ])
    setNewAssessmentName("")
    setAddingAssessment(null)
  }

  const toggleVisit = (assessmentIdx: number, visitPosition: number) => {
    const updated = [...assessments]
    const current = updated[assessmentIdx].scheduled_visits || []
    updated[assessmentIdx] = {
      ...updated[assessmentIdx],
      scheduled_visits: current.includes(visitPosition)
        ? current.filter((v) => v !== visitPosition)
        : [...current, visitPosition],
    }
    setValue(`parts.${partIndex}.assessments`, updated)
  }

  const deleteAssessment = (idx: number) => {
    setValue(
      `parts.${partIndex}.assessments`,
      assessments.filter((_, i) => i !== idx),
    )
  }

  const updateAssessment = (idx: number, field: keyof ProtocolAssessment, value: string) => {
    const updated = [...assessments]
    updated[idx] = { ...updated[idx], [field]: value }
    setValue(`parts.${partIndex}.assessments`, updated)
  }

  return {
    // Data
    periods,
    assessments,
    allVisits,
    categories,
    theadRef,
    theadHeight,

    // UI state
    newCategoryName,
    addingAssessment,
    newAssessmentName,
    editingCell,
    cellDraft,
    showSavePrompt,
    editingPeriod,
    editingCategory,
    hoveringVisit,

    // State setters
    setNewCategoryName,
    setAddingAssessment,
    setNewAssessmentName,
    setCellDraft,
    setEditingPeriod,
    setEditingCategory,
    setHoveringVisit,

    // Handlers
    startCellEdit,
    commitCellEdit,
    cancelCellEdit,
    keepEditingCell,
    addPeriod,
    renamePeriod,
    deletePeriod,
    addVisit,
    updateVisit,
    deleteVisit,
    addCategory,
    renameNewCategory,
    renameCategory,
    deleteCategory,
    addAssessmentToCategory,
    toggleVisit,
    deleteAssessment,
    updateAssessment,
  }
}
